import { useState } from 'react'

import type { SavedConfigsApi } from '@/app/hooks/useQuantSavedConfigs'
import { findSavedConfig } from '@/app/lib/quant/savedConfigs'
import type { ParamValue, SavedConfig } from '@/app/lib/quant/types'
import { Button } from '@/ui/Button'
import { ConfirmButton } from '@/ui/ConfirmButton'
import { Select } from '@/ui/Select'

export interface QuantSavedConfigsProps {
  slug: string
  saved: SavedConfigsApi
  /** The form's current raw values: what Save stores (valid or not, it is the user's draft). */
  raw: Record<string, ParamValue>
  onLoad: (config: SavedConfig) => void
  emit: (kind: 'save' | 'err', text: string) => void
}

type Mode = 'idle' | 'naming' | 'overwrite'

/**
 * Spec 073 B3: the saved-config row of the Config card. Fixed height (the inline error has its own
 * reserved line), non-optimistic: every change is confirmed by the server and the list refetched.
 * Accessible names are fixed literals; a config name only ever appears as visible text.
 */
export function QuantSavedConfigs({ slug, saved, raw, onLoad, emit }: QuantSavedConfigsProps) {
  const [selected, setSelected] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('idle')
  const [nameInput, setNameInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { configs } = saved
  const active = selected !== null && findSavedConfig(configs, selected) !== undefined ? selected : null
  const empty = configs.length === 0
  const name = nameInput.trim()

  function load() {
    const config = active === null ? undefined : findSavedConfig(configs, active)
    if (config === undefined) return
    setError(null)
    onLoad(config)
    emit('save', `Loaded saved config ${config.name} into the ${slug} form.`)
  }

  function startNaming() {
    setError(null)
    setNameInput(active ?? '')
    setMode('naming')
  }

  function cancel() {
    setMode('idle')
    setError(null)
  }

  async function write(target: string) {
    setBusy(true)
    const failure = await saved.save(target, raw)
    setBusy(false)
    if (failure !== null) {
      setError(failure)
      emit('err', `Config not saved - ${failure}`)
      setMode('naming')
      return
    }
    setSelected(target)
    setMode('idle')
    setError(null)
    emit('save', `Saved config ${target} for ${slug}.`)
  }

  function submit() {
    if (name === '' || busy) return
    if (findSavedConfig(configs, name) !== undefined) {
      setMode('overwrite')
      return
    }
    void write(name)
  }

  async function remove() {
    if (active === null) return
    const target = active
    setBusy(true)
    const failure = await saved.remove(target)
    setBusy(false)
    if (failure !== null) {
      setError(failure)
      emit('err', `Config not deleted - ${failure}`)
      return
    }
    setSelected(null)
    setError(null)
    emit('save', `Deleted saved config ${target}.`)
  }

  return (
    <div data-saved-configs className="border-b border-ink-20 px-[18px] py-2">
      <div className="flex h-9 items-center gap-2">
        {mode === 'idle' && (
          <>
            <div className="w-[240px] shrink-0">
              <Select
                ariaLabel="Saved configs"
                value={active}
                onValueChange={(next) => {
                  setSelected(next)
                  setError(null)
                }}
                options={configs.map((c) => ({ value: c.name, label: c.name }))}
                placeholder={empty ? 'No saved configs' : 'Select a saved config'}
                disabled={empty || busy}
              />
            </div>
            <Button variant="secondary" size="sm" aria-label="Load config" disabled={active === null || busy} onClick={load} className="text-ink">
              Load
            </Button>
            <Button variant="secondary" size="sm" aria-label="Save config" disabled={busy} onClick={startNaming} className="text-ink">
              Save
            </Button>
            <ConfirmButton
              label="DELETE"
              size="sm"
              ariaLabel="Delete config"
              armedAriaLabel="Confirm delete config"
              disabled={active === null || busy}
              onConfirm={() => void remove()}
              className="text-ink"
            />
          </>
        )}
        {mode === 'naming' && (
          <>
            <input
              type="text"
              aria-label="Config name"
              value={nameInput}
              maxLength={64}
              autoFocus
              onChange={(event) => setNameInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit()
                else if (event.key === 'Escape') cancel()
              }}
              placeholder="Name this config"
              className="h-9 w-[240px] shrink-0 border border-ink-25 bg-transparent px-2.5 font-mono text-[12px] text-ink focus:border-accent-fill focus:outline-none"
            />
            <Button size="sm" aria-label="Store config" disabled={name === '' || busy} onClick={submit}>
              Save
            </Button>
            <Button variant="secondary" size="sm" aria-label="Cancel save" disabled={busy} onClick={cancel} className="text-ink">
              Cancel
            </Button>
          </>
        )}
        {mode === 'overwrite' && (
          <>
            <span data-overwrite-prompt className="min-w-0 flex-1 truncate font-mono text-[12px] text-ink" title={`Overwrite "${name}"?`}>
              Overwrite &quot;{name}&quot;?
            </span>
            <Button size="sm" variant="destructive" aria-label="Overwrite config" disabled={busy} onClick={() => void write(name)}>
              Overwrite
            </Button>
            <Button variant="secondary" size="sm" aria-label="Keep existing config" disabled={busy} onClick={() => setMode('naming')} className="text-ink">
              Cancel
            </Button>
          </>
        )}
      </div>
      <div
        role={error === null ? undefined : 'alert'}
        data-saved-configs-error
        title={error ?? undefined}
        className="h-4 truncate font-mono text-[11px] text-alert"
      >
        {error ?? (saved.state.status === 'error' ? saved.state.message : null)}
      </div>
    </div>
  )
}
